import { api } from '@api/client';

/**
 * Admin panel API wrappers.
 *
 * Reuses the shared axios instance (withCredentials: true) so the PHP session
 * cookie set at login is automatically sent on every admin request.
 *
 * Add new feature groups here as the panel grows (content, registrations, …).
 */

export const adminApi = {
  auth: {
    login:  (credentials) => api.post('/api/admin/auth/login', credentials),
    logout: ()            => api.post('/api/admin/auth/logout'),
    me:     ()            => api.get('/api/admin/auth/me'),
  },

  analytics: {
    // Aggregations can be heavy on large visit tables, so allow a longer window
    // than the shared 30s default before giving up.
    overview: (days = 14) => api.get('/api/admin/analytics/overview', { params: { days }, timeout: 90_000 }),
    /**
     * Period-scoped dashboard data.
     * @param {object} opts
     * @param {string} [opts.cards]   card window: today|yesterday|7d|30d|all
     * @param {string} [opts.graph]   graph window: today|yesterday|7d|14d|30d
     * @param {string} [opts.compare] comparison start date (YYYY-MM-DD), optional
     */
    dashboard: ({ cards = '7d', graph = '14d', compare } = {}) =>
      api.get('/api/admin/analytics/dashboard', {
        params: { cards, graph, ...(compare ? { compare } : {}) },
        timeout: 90_000,
      }),
  },

  overview: {
    stats: () => api.get('/api/admin/overview/stats'),
  },

  speakers: {
    list:   ()         => api.get('/api/admin/speakers/list'),
    // formData (multipart) so an optional photo can be attached.
    save:   (formData) => api.post('/api/admin/speakers/save', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
    delete: (id)       => api.post('/api/admin/speakers/delete', { id }),
  },

  /**
   * Editable page-content blocks (CFP, Registration, Gallery, Digital).
   * Each block is stored as a JSON document keyed by slug; the public site
   * merges it over its built-in defaults.
   */
  content: {
    get:    (key)          => api.get('/api/admin/content/get', { params: { key } }),
    save:   (key, value)   => api.post('/api/admin/content/save', { key, value }),
    // Upload one image; resolves to { path } (relative, e.g. upload/content/x.jpg).
    upload: (file) => {
      const fd = new FormData();
      fd.append('file', file);
      return api.post('/api/admin/content/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 180_000,
      });
    },
    // Upload one downloadable document (PDF, ZIP, …); resolves to { path }.
    uploadFile: (file) => {
      const fd = new FormData();
      fd.append('file', file);
      return api.post('/api/admin/content/upload-file', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    },
  },

  joinUs: {
    list:   ()   => api.get('/api/admin/join-us/list'),
    delete: (id) => api.post('/api/admin/join-us/delete', { id }),
  },

  contact: {
    list:   ()   => api.get('/api/admin/contact/list'),
    delete: (id) => api.post('/api/admin/contact/delete', { id }),
  },

  feedback: {
    list:   ()   => api.get('/api/admin/feedback/list'),
    delete: (id) => api.post('/api/admin/feedback/delete', { id }),
  },

  testimonials: {
    list:    ()                  => api.get('/api/admin/testimonials/list'),
    publish: (id, published)     => api.post('/api/admin/testimonials/publish', { id, published }),
    delete:  (id)                => api.post('/api/admin/testimonials/delete', { id }),
  },

  registrations: {
    list: () => api.get('/api/admin/registrations/list'),
    uploadReceipt: (id, file) => {
      const fd = new FormData();
      fd.append('id', String(id));
      fd.append('file', file);
      return api.post('/api/admin/registrations/receipt-save', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60_000,
      });
    },
    // Each PDF is matched to a Paper ID by its filename, so a folder of
    // receipts can be attached in one pass.
    bulkReceipts: (files) => {
      const fd = new FormData();
      Array.from(files).forEach((file) => fd.append('files[]', file));
      return api.post('/api/admin/registrations/receipts-bulk', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 180_000,
      });
    },
    saveFinderEmails: (id, emails) => api.post('/api/admin/registrations/emails-save', { id, emails }),
  },

  certificates: {
    list: () => api.get('/api/admin/certificates/list'),
    save: (data) => api.post('/api/admin/certificates/save', data),
    uploadZip: (paperId, file) => {
      const fd = new FormData();
      fd.append('paper_id', String(paperId));
      fd.append('file', file);
      return api.post('/api/admin/certificates/zip-save', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120_000,
      });
    },
    bulkZips: (files) => {
      const fd = new FormData();
      Array.from(files).forEach((file) => fd.append('files[]', file));
      return api.post('/api/admin/certificates/zip-bulk', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 180_000,
      });
    },
    delete: (paperId, opts = {}) => api.post('/api/admin/certificates/delete', {
      paper_id: paperId,
      zip: opts.zip !== false,
      emails: opts.emails !== false,
    }),
    download: (paperId) => api.get('/api/admin/certificates/download', {
      params: { paper_id: paperId },
      responseType: 'blob',
      timeout: 120_000,
    }),
  },

  pricing: {
    plans:        ()     => api.get('/api/admin/pricing/plans'),
    savePlan:     (data) => api.post('/api/admin/pricing/plan-save', data),
    deletePlan:   (id)   => api.post('/api/admin/pricing/plan-delete', { id }),
    rotatePlan:   (id)   => api.post('/api/admin/pricing/plan-rotate', { id }),
    settings:     ()     => api.get('/api/admin/pricing/settings'),
    saveSettings: (data) => api.post('/api/admin/pricing/settings-save', data),
  },

  inrPaperIds: {
    list:   ()                 => api.get('/api/admin/inr-paper-ids/list'),
    save:   ({ paper_ids, note }) => api.post('/api/admin/inr-paper-ids/save', { paper_ids, note }),
    delete: (id)               => api.post('/api/admin/inr-paper-ids/delete', { id }),
  },

  offers: {
    list:   (currency = 'USD') => api.get('/api/admin/offers/list', { params: { currency } }),
    save:   (data)             => api.post('/api/admin/offers/save', data),
    delete: (id)               => api.post('/api/admin/offers/delete', { id }),
  },

  participation: {
    sessions:          ()     => api.get('/api/admin/participation/sessions'),
    saveSession:       (data) => api.post('/api/admin/participation/session-save', data),
    deleteSession:     (id)   => api.post('/api/admin/participation/session-delete', { id }),
    submissions:       ()     => api.get('/api/admin/participation/submissions'),
    // "delete" archives the submission (30-day retention before auto-purge).
    // Every action takes one id or a list of ids (multi-select in the panel).
    archiveSubmission: (id)   => api.post('/api/admin/participation/submission-delete', { id }),
    archiveSubmissions: (ids) => api.post('/api/admin/participation/submission-delete', { ids }),
    archived:          ()     => api.get('/api/admin/participation/archive'),
    restoreSubmission: (id)   => api.post('/api/admin/participation/submission-restore', { id }),
    restoreSubmissions: (ids) => api.post('/api/admin/participation/submission-restore', { ids }),
    purgeSubmission:   (id)   => api.post('/api/admin/participation/submission-purge', { id }),
    purgeSubmissions:  (ids)  => api.post('/api/admin/participation/submission-purge', { ids }),
    downloadForm:      (id, type) => api.get('/api/admin/participation/download', {
      params: { id, ...(type ? { type } : {}) },
      responseType: 'blob',
      timeout: 60_000,
    }),
    downloadZip:       (ids)  => api.post('/api/admin/participation/download-zip', { ids }, {
      responseType: 'blob',
      timeout: 180_000,
    }),
  },
};

/**
 * Public page-view tracker (no auth). Best-effort: failures are swallowed so a
 * tracking hiccup never affects the visitor's experience.
 *
 * The browser timezone is sent as a privacy-friendly, coarse location signal
 * (e.g. "Asia/Bangkok") — no IP geolocation is performed or stored.
 */
export function trackPageView({ path, referrer, title }) {
  let timezone = '';
  try {
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch {
    timezone = '';
  }
  return api
    .post('/api/analytics/track', { path, referrer, title, timezone })
    .catch(() => {});

}
